import React from 'react'
import styled from 'styled-components'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import CloseIcon from '~/components/icons/ui/64px_small-remove.svg'
import MenuIcon from '~/components/icons/ui/48px_menu-34.svg'
import useAPI from '~/prix/react/hooks/api'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { MapType } from '~/components/map/mapContext'
import { StyleForLayer } from './styleForLayer.util'
import LayerLegendComponent from './layerLegend.component'
import { useClickAway } from 'react-use'
import { VariableWithConclusion } from '~/packages/general/variable/variables.typing'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import variableListDefinition from '~/packages/general/variable/list/variableList.definition'
import { useNavigate } from 'react-router'
import { isVariableLayer } from './clusterListInput.util'

const RightButtonsTopBoxWrapper = styled.button`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0;
  align-items: center;
  background: transparent;
  box-shadow: none;
  border: none;
  border-radius: 0;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  transition-duration: ${({ theme }) => theme.transitionDurationMs}ms;
  transition-timing-function: ${({ theme }) => theme.transitionTimingFunction};
  transition-property: background-color, box-shadow;
  text-align: left;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.primary};
  width: 100%;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadow(2)};
    z-index: 1;
  }

  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  svg {
    fill: #0c2340;
  }
`

interface NavigationMenuProps {
  $isOpen: boolean
}

const NavigationMenu = styled.nav<NavigationMenuProps>`
  z-index: 10000;
  position: absolute;
  top: 105%;
  right: 0;
  width: 284px;
  background-color: white;
  font-size: 1rem;
  font-weight: normal;
  display: flex;
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  flex-direction: column;
  border-radius: 8px;
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '-30%')});
  transition:
    transform 0.3s ease-in-out,
    opacity ${({ $isOpen }) => ($isOpen ? '0.25s' : '0s')} ease-in-out;
  box-shadow: ${({ theme }) => theme.shadow(1)};
  gap: ${({ theme }) => theme.padding(0.3)};
  padding: ${({ theme }) => theme.padding(1)};
  color: #414141;

  @media (max-width: ${({ theme }) => theme.breakpoints?.mobile}px) {
    overflow: scroll;
    max-height: 100%;
    min-height: 60vh;
  }

  button {
    position: relative;
    cursor: pointer;
    background-color: red;
    transition-duration: ${({ theme }) => theme.transitionDurationMs}ms;
    transition-timing-function: ${({ theme }) => theme.transitionTimingFunction};
    transition-property: background-color;
    background: transparent;
    width: 100%;
    list-style: none;
    color: inherit;
    border: none;
    text-align: left;
    padding: ${({ theme }) => theme.padding(0.3)};

    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      box-shadow: ${({ theme }) => theme.shadow(1)};
      opacity: 0;
      transition: opacity ${({ theme }) => theme.transitionDurationMs}ms
        ${({ theme }) => theme.transitionTimingFunction};
    }

    &:hover {
      background-color: #fff;

      &::before {
        opacity: 1;
      }
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(100, 150, 250, 0.6);
    }
  }

  button:hover {
    background-color: #f3f3f2;
  }

  hr {
    margin: 0.5rem 0;
    border: none;
    border-top: 2px solid rgba(0, 0, 0, 0.1);
  }

  .legends-wrapper {
    display: none;
    flex-direction: column;

    @media (max-width: ${({ theme }) => theme.breakpoints?.mobile}px) {
      display: flex;
    }

    .legends {
      display: flex;
      flex-direction: column;
      padding: ${({ theme }) => theme.padding(0.3)} 0;
      gap: ${({ theme }) => theme.padding(1)};
    }
  }
`

const Option = styled.button`
  position: relative;
  cursor: pointer;
  background-color: red;
  transition-duration: ${({ theme }) => theme.transitionDurationMs}ms;
  transition-timing-function: ${({ theme }) => theme.transitionTimingFunction};
  transition-property: background-color;
  background: transparent;
  width: 100%;
  list-style: none;
  color: inherit;
  border: none;
  text-align: left;
  padding: 0.3rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    box-shadow: ${({ theme }) => theme.shadow(1)};
    opacity: 0;
    transition: opacity ${({ theme }) => theme.transitionDurationMs}ms
      ${({ theme }) => theme.transitionTimingFunction};
  }

  &:hover {
    background-color: #fff;

    &::before {
      opacity: 1;
    }
  }

  &.is-selected,
  &.is-selected:hover {
    background-color: #f3f3f3;
  }

  &:hover {
    background-color: #f3f3f2;
  }
`

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px;
`

export const TextDivider = styled.p`
  text-align: center;
  font-size: 0.8rem;
  font-weight: normal;
  margin: 0;
  color: #7d7d7d;
`

const mapTypes: MapType[] = ['roadmap', 'hybrid', 'terrain']
const mapTypesLabels = {
  roadmap: 'Mapa',
  satellite: 'Satélite sem rótulos',
  terrain: 'Terreno',
  hybrid: 'Satélite',
}

export type Map3DOption = 'on' | 'whenNecessary' | 'off'
const map3DOptions: Map3DOption[] = ['on', 'whenNecessary', 'off']
const map3DOptionsLabels = {
  on: 'Habilitado',
  whenNecessary: 'Quando necessário',
  off: 'Desabilitado',
}

export interface RightButtonsTopBoxProps {
  onChangeMapType: (newMapType: MapType) => void
  mapType: MapType
  onChangeMap3DOption: (newMap3DOption: Map3DOption) => void
  map3DOption: Map3DOption
  layers: Layer[]
  variables: VariableWithConclusion[]
  setBaseLayers: (layers: Layer[] | ((layers: Layer[]) => Layer[])) => void
  styles: StyleForLayer[]
  children?: React.ReactNode
  keepMenuIcon?: boolean
  setIsSomethingLoading?: (isLoading: boolean) => void
}

export default function RightButtonsTopBox({
  onChangeMapType,
  mapType,
  onChangeMap3DOption,
  map3DOption,
  layers,
  variables,
  setBaseLayers,
  styles,
  keepMenuIcon,
}: RightButtonsTopBoxProps) {
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  const ref = React.useRef<HTMLDivElement>(null)
  const { context, isLoading: isLoadingAPI } = useAPI()
  const { callAction: callVariableList } = useActionFunction(variableListDefinition)
  const preferredGeneralSettings = context.user?.activeClient?.settings?.general
  const navigate = useNavigate()

  const canAddLayer = true
  const openMenu = () => setIsOpen(prevData => !prevData)

  useClickAway(ref, () => {
    if (isOpen) setIsOpen(false)
  })

  const handleAddExtraLayer = async () => {
    let newVariable: VariableWithConclusion | undefined

    const baseLayer = layers[0]
    if (!baseLayer || !isVariableLayer(baseLayer)) {
      return
    }

    const baseVariable = baseLayer.variable
    const isDifferentVariable = (variable: VariableWithConclusion) =>
      variable.key !== baseVariable.key || variable.presetKey !== baseVariable.presetKey

    newVariable =
      variables.find(
        variable =>
          variable.categoryKey === baseVariable.categoryKey &&
          variable.actionDefinitionKey === baseVariable.actionDefinitionKey &&
          isDifferentVariable(variable),
      ) ||
      variables.find(
        variable =>
          variable.categoryKey === baseVariable.categoryKey &&
          variable.timeType === baseVariable.timeType &&
          isDifferentVariable(variable),
      ) ||
      variables.find(
        variable =>
          variable.actionDefinitionKey === baseVariable.actionDefinitionKey &&
          isDifferentVariable(variable),
      )

    if (!newVariable) {
      try {
        const pageSize = 50
        let startIndex = 0
        let endIndex = pageSize
        let safeguard = 500

        while (!newVariable && safeguard > 0) {
          const result = callVariableList({
            startIndex,
            endIndex,
            category: String(baseVariable.categoryKey || ''),
            preferredSpaceDelimitationType:
              preferredGeneralSettings?.preferredSpaceDelimitationType,
            preferredSpaceDelimitationId: preferredGeneralSettings?.preferredSpaceDelimitationId,
          })
          const { items = [], pagesLeft = 0 } = await result.resultPromise

          if (items && Array.isArray(items)) {
            newVariable =
              items.find(
                item =>
                  item.categoryKey === baseVariable.categoryKey &&
                  item.actionDefinitionKey === baseVariable.actionDefinitionKey &&
                  isDifferentVariable(item),
              ) ||
              items.find(
                item =>
                  item.categoryKey === baseVariable.categoryKey &&
                  item.timeType === baseVariable.timeType &&
                  isDifferentVariable(item),
              )
          }

          if (newVariable) break
          if (pagesLeft <= 0) break

          startIndex += pageSize
          endIndex += pageSize
          safeguard -= 1
        }
      } catch (error) {
        console.debug('erro buscando variável por paginação', error)
      }
    }

    if (!newVariable) {
      return
    }

    setBaseLayers([
      baseLayer,
      { variable: newVariable, space: baseLayer.space, time: baseLayer.time },
    ])
  }

  const handleRemoveExtraLayer = () => {
    setBaseLayers([layers[0]])
  }

  return (
    <div
      ref={ref}
      style={{
        placeContent: 'center',
        display: 'flex',
        width: '100%',
        height: '100%',
      }}
    >
      <NavigationMenu $isOpen={isOpen}>
        <TextDivider>Tipo de mapa</TextDivider>
        {mapTypes.map(mapTypeOption => (
          <MenuButton key={mapTypeOption} onClick={() => onChangeMapType(mapTypeOption as MapType)}>
            {mapType === mapTypeOption && (
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#0C2340' }} />
            )}
            {mapTypesLabels[mapTypeOption as keyof typeof mapTypesLabels]}
          </MenuButton>
        ))}
        <hr />

        <TextDivider>Mapa 3D</TextDivider>
        {map3DOptions.map(map3DOptionOption => (
          <MenuButton
            key={map3DOptionOption}
            onClick={() => onChangeMap3DOption(map3DOptionOption as Map3DOption)}
          >
            {map3DOption === map3DOptionOption && (
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#0C2340' }} />
            )}
            {map3DOptionsLabels[map3DOptionOption]}
          </MenuButton>
        ))}

        {canAddLayer && (
          <>
            <hr />
            <TextDivider>Camada adicional</TextDivider>
            {layers.length === 1 ? (
              <Option
                disabled={isLoadingAPI}
                onClick={handleAddExtraLayer}
                style={{ cursor: isLoadingAPI ? 'not-allowed' : 'pointer' }}
              >
                Adicionar camada
              </Option>
            ) : (
              <Option
                disabled={isLoadingAPI}
                onClick={handleRemoveExtraLayer}
                style={{ cursor: isLoadingAPI ? 'not-allowed' : 'pointer' }}
              >
                Remover camada
              </Option>
            )}
          </>
        )}

        <div className='legends-wrapper'>
          <hr />

          <TextDivider>Legenda{layers.length > 1 ? 's' : ''}</TextDivider>

          <div className='legends'>
            {layers.map((layer, index) => {
              const style = styles[index]
              return (
                <React.Fragment key={index}>
                  <LayerLegendComponent
                    width='100%'
                    height={layers.length === 2 && index === 0 ? '50px' : 'auto'}
                    layer={layer}
                    style={style}
                  />
                </React.Fragment>
              )
            })}
          </div>
        </div>

        <hr />
        <TextDivider>Geral</TextDivider>
        <MenuButton onClick={() => navigate('/app/questionnaire/list', { replace: true })}>
          Gerenciamento
        </MenuButton>
      </NavigationMenu>
      <RightButtonsTopBoxWrapper className='md-button' onClick={() => openMenu()}>
        {isOpen && !keepMenuIcon ? (
          <CloseIcon width={20} height={20} />
        ) : (
          <MenuIcon width={20} height={20} />
        )}
      </RightButtonsTopBoxWrapper>
    </div>
  )
}
